import BasePopup from './BasePopup';
import { gsap } from 'gsap';
import EntryPoint from 'Engine/EntryPoint';
import GlobalDispatcher from 'Engine/events/GlobalDispatcher';
import BaseMoneyFormat from 'Engine/base/moneyFormat/MoneyFormat';
import SoundManager from 'Engine/soundManager/SoundManager';
import { eSounds } from '../../enums/eSounds';
import eEventTypes from '../../enums/eEventTypes';
import WinAmountAnimated from '../../winAmountAnimated/selfResizedWin';
import ControllerLabelsThrower from '../../slot/features/controllerLabelsThrower';
import { mainGoldFont } from '../../fontStyles';
import { eWinAmountSizes } from '../../enums/eWinAmountSizes';

export default class ControllerWinCollect extends BasePopup {
  constructor(data, displayData) {
    super(data, displayData);

    this._wrapperOffsets = {
      'H': { x: 70, y: 0 },
      'V': { x: 0, y: -50 },
    };

    this._idleDuration = 2000;
    this.lastWinAmount = 0;
    this._bounceValue = 0;

    this.init();
  }

  init() {
    super.init();
    this.overlay = this.getChildByName('overlay');

    this.winAmountField = this.wrapper.getChildByName('amount');
    this.multiplier = this.wrapper.getChildByName('multiplier');
    this.multiplierValue = this.multiplier.getChildByName('value');
    this.multiplierValue.style = { ...this.multiplierValue.style, ...mainGoldFont };
    this.multiplierValue.correctPosition('center', 'center');
    this.wrapper.setChildIndex(this.winAmountField, this.wrapper.children.length - 1);

    this.winAmountCounter = new WinAmountAnimated(this.winAmountField, { fontSize: eWinAmountSizes.H.REGULAR });
    this.winAmountCounter.decimals = EntryPoint.GameModel.currencyInfo.decimals;
  }

  addListeners() {
    super.addListeners();
    GlobalDispatcher.add(eEventTypes.EET_WIN_COLLECT__START, this.show.bind(this));
    GlobalDispatcher.add(eEventTypes.EET_WIN_MULTIPLY__START, this._animateMultiplierWin.bind(this));
    GlobalDispatcher.add(eEventTypes.EET_SEND_MULTIPLIER_TEXT_FIELD, this._updateTargetMultiplier.bind(this));
    GlobalDispatcher.add(eEventTypes.EET_THROW_LABEL, this.onLabelProvided.bind(this));
  }

  onGameResized(data) {
    if (data.isLandscape) {
      if (this.background) this.background.goToFrame(2);
    } else if (data.isMobile && data.isPortrait) {
      if (this.background) this.background.goToFrame(1);
    }

    this.wrapper.anchorDefault();
    this.wrapper.x += this._wrapperOffsets[data.orientation].x;
    this.wrapper.y += this._wrapperOffsets[data.orientation].y;

    if (this._targetMultipler) {
      this._multiplierStartPosition = this.wrapper.toLocal({ x: 0, y: 0 }, this._targetMultipler);
    }
  }

  onLabelProvided(data) {
    this.ControllerLabelsThrower.updateTargetContainer(this);
    this.ControllerLabelsThrower.throw(data);
  }

  show() {
    this.alpha = 1;
    this.overlay.alpha = 0;

    this.winAmountCounter.y = this._bounceValue;
    this.winAmountCounter.set(0);

    this.visible = true;
    gsap.to(this.overlay, { alpha: 1, duration: 2 });
  }

  hide() {
    super.hide();
  }

  onHide() {
    this._reset();
    this.winAmountField.alpha = 0;
    GlobalDispatcher.dispatch(eEventTypes.EET_WIN_MULTIPLY__COMPLETE);
  }

  add(value, isLast) {
    const nextAmount = isLast ? EntryPoint.GameModel.bonusField.total_win : this.lastWinAmount + value;
    this.winAmountCounter.animate(this.lastWinAmount, nextAmount, 200);
    this.lastWinAmount = nextAmount;
    SoundManager.play(eSounds.EST_COIN.soundName, eSounds.EST_COIN.volume, false, eSounds.EST_COIN.group);

    gsap.to(this.winAmountField, { alpha: 1, duration: 0.2 });
  }

  stopMainBackgroundSound() {
  }

  playMainBackgroundSound() {
  }

  _updateTargetMultiplier({ params: multiplier }) {
    this._targetMultipler = multiplier;
    this._multiplierStartPosition = this.wrapper.toLocal({ x: 0, y: 0 }, multiplier);
  }

  _animateMultiplierWin() {
    if (EntryPoint.GameModel.bonusField.multiplier <= 1) return this.startIdle();

    this._multiplierAnimation = gsap.timeline();

    this._targetMultipler.visible = false;
    this.multiplier.x = this._multiplierStartPosition.x;
    this.multiplier.y = this._multiplierStartPosition.y;
    this.multiplierValue.text = `×${EntryPoint.GameModel.bonusField.multiplier}`;
    this.multiplier.visible = true;

    this._multiplierAnimation.fromTo(this.multiplier.scale, { x: 1, y: 1 }, {
      x: 2,
      y: 2,
      duration: 0.4,
      ease: 'power1.in'
    }, 'start').to(this.multiplier, {
      x: 0,
      duration: 0.4,
      ease: 'power1.in'
    }, '<').to(this.multiplier, {
      y: this.winAmountField.y + this._bounceValue,
      duration: 0.3,
      ease: 'power4.in'
    }, '+=0.1').fromTo(this.multiplier, { alpha: 1 }, {
      alpha: 0,
      duration: 0.25,
      ease: 'power1.in',
      onComplete: () => {
        this.multiplier.visible = false;
        this.multiplier.alpha = 1;
      },
    }, '-=0.2').call(() => {
      this.totalWin = EntryPoint.GameModel.bonusField.total_win_multiplied;
      SoundManager.play(eSounds.EST_WIN_MULTIPLY.soundName, eSounds.EST_WIN_MULTIPLY.volume, false, eSounds.EST_WIN_MULTIPLY.group);
    }, [], 'jumpDown').to(this.winAmountField, {
      y: '+=100',
      duration: 0.2,
      ease: 'back.out(1.4)'
    }, 'jumpDown').to(this.winAmountField, {
      y: '-=100',
      duration: 0.3,
      ease: 'power4.out'
    }, 'jumpUp').call(() => this.startIdle(), [], 'idle');
  }

  _reset() {
    this.lastWinAmount = 0;
    this.totalWin = 0;
  }

  get valueContainer() {
    return this.winAmountField;
  }

  set totalWin(value) {
    if (typeof value === 'string') value = parseFloat(value);
    this.winAmountCounter.stop();
    this.winAmountCounter.set(value);
  }

  get ControllerLabelsThrower() {
    if (!this._controllerLabelsThrower) {
      const container = new PIXI.Container();
      this.wrapper.addChildAt(container, 0);
      this._controllerLabelsThrower = new ControllerLabelsThrower(container);
    }
    return this._controllerLabelsThrower;
  }
}
